<script setup lang="ts">
import type { LegalAreaProps } from './LegalArea.props'
import type { UILinkProps } from '@design-system/components/UI/Link/UILink.props'

const props = defineProps<LegalAreaProps>()

const links = computed(() => props.data?.content?.links ?? [])
const content = computed(() => props.data?.content?.body ?? [])

const isPreface = <ContentItem extends { id?: string }>(
  contentItem: ContentItem
) => !contentItem.id

const prefaceText = computed(() => content?.value?.find(isPreface)?.text)

const filteredContent = computed(() => {
  const data = content.value.filter(
    (contentItem: any) => !isPreface(contentItem)
  )
  const filteredContent = links.value.map(link => {
    const contentItem = data.find(
      (contentItem: any) => contentItem.id === link.target.target
    )
    return {
      id: link.target.target,
      label: link.target.label,
      text: contentItem?.text,
    }
  })

  return filteredContent
})

const print = () => {
  if (!window.print) {
    return
  }
  window.print()
}
</script>

<template>
  <div class="gap-md md:gap-xxl flex flex-col">
    <ol class="gap-xs md:gap-sm ml-6 flex list-decimal flex-col">
      <li
        v-for="link in links"
        :key="link?.target?.label"
        class="text-link-6 w-fit"
      >
        <UILink
          v-bind="link.target as UILinkProps"
          class="h-auto w-full text-left underline md:w-fit"
          external
          aria-current="false"
        >
          <UtilsMarkdown
            v-if="link?.target?.label"
            :content="link?.target?.label"
            container="span"
            class="text-link-6"
          />
        </UILink>
      </li>
    </ol>

    <div
      v-if="props.data?.content"
      class="p-md md:p-xxl gap-md lg:gap-xxl bg-neutral-white text-neutral-black markdown-legal-area flex flex-col"
    >
      <UtilsArticleMarkdown
        v-if="prefaceText"
        :id="'prefaceText'"
        :content="prefaceText"
        class="page-structure"
      />

      <div
        v-for="(content, index) in filteredContent"
        :key="content.id"
        class="gap-md flex flex-col"
      >
        <UtilsMarkdown
          v-if="content.label"
          :id="content.id"
          :content="`<span class='flex flex-row gap-2 items-center'> ${index + 1}. ${content.label}</span>`"
          container="h2"
          class="legal-area-heading"
        />

        <UtilsArticleMarkdown
          v-if="content.text"
          :content="content.text"
          class="page-structure [&>*]:break-words"
        />
      </div>
      <AtomsCta anatomy="secondary" class="print:hidden" @click-handler="print">
        <template #label> {{ $ts('legalArea.print') }} </template>
      </AtomsCta>
    </div>
    <div v-else class="bg-neutral-white text-neutral-black min-h-96"></div>
  </div>
</template>

<style scoped lang="scss">
.legal-area-heading {
  scroll-margin-top: 1.5rem;

  @screen lg {
    scroll-margin-top: calc(var(--header-default-height) + 1.5rem);
  }
}

.page-structure {
  :deep(table) {
    @apply my-5 block w-full overflow-x-scroll;

    ul {
      @apply pt-0;
    }
  }

  :deep(th) {
    @apply text-medium-5 min-w-[231px] whitespace-normal break-words px-3 py-2 text-left;
  }

  :deep(td) {
    @apply px-3 py-2;
  }

  :deep(li),
  :deep(td),
  :deep(p) {
    @apply whitespace-normal break-words text-left;
    @apply text-book-6;
  }

  /* All p, headings, tables and lists have top-bottom margins by default*/
  :deep(p),
  :deep(h4),
  :deep(h5) {
    @apply my-4;
  }

  :deep(h3) {
    @apply my-5;
  }

  :deep(h2) {
    @apply my-6;
  }

  /* Margins are removed for the first and last elements inside each page-structure block, to avoid adding it ot the gap */
  :deep(:first-child, ul:first-child, ol:first-child) {
    @apply mt-0 !important;
  }
  :deep(:last-child, ul:last-child, ol:last-child) {
    @apply mb-0 !important;
  }
}

.markdown-legal-area {
  :deep(ul) {
    @apply my-5 list-disc pl-9 !important;
  }
  :deep(ol) {
    @apply my-5 list-decimal pl-9 !important;
  }
  :deep(a) {
    text-decoration: underline;
  }
}
</style>
